import { toast } from "sonner";

/**
 * Copy text to clipboard with graceful fallbacks.
 * 1. Tries navigator.clipboard.writeText (modern, requires secure context + permission)
 * 2. Falls back to document.execCommand('copy') via hidden textarea (older browsers + iOS)
 * 3. If both fail, surfaces a friendly toast and returns false (no red error screen)
 *
 * Returns: boolean — true on success, false on failure
 */
export async function copyToClipboard(text, { successMsg = "Copied!", failMsg = "Long press to copy." } = {}) {
  if (!text) {
    toast.error("Nothing to copy");
    return false;
  }
  // Path 1 — modern clipboard API (only on secure context / granted permission)
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(String(text));
      toast.success(successMsg);
      return true;
    }
  } catch (err) {
    console.warn("Clipboard API unavailable, falling back to execCommand", err);
    // fall through to legacy method
  }

  // Path 2 — legacy fallback via execCommand (works on most mobile browsers + http)
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text);
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) {
      toast.success(successMsg);
      return true;
    }
  } catch (err) {
    console.warn("Legacy clipboard fallback failed", err);
    // ignored — surface friendly error below
  }

  toast.error(failMsg);
  return false;
}
